import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input, Collapse, Button, Modal, message, Tabs } from 'antd'
import { BgColorsOutlined, EditOutlined } from '@ant-design/icons'
import sensors, { parsePlaceValue } from '../../../../utils/sensors'
import parseQueryString from '../../../../utils/parseQueryString'
import {
  setEditorWatermark,
  setEditorActiveItem,
  setExpandedPanels,
  updateOrNewWatermark,
  watermarkGrantTips,
  fetchGroupWatermarks,
  saveWatermarkItemsList
} from '../../../../actions/watermark'

import { ITEM_ID, WATERMARK_CONFIG } from '../../../../constants/watermark'
import {
  // ThemePanel,
  ThemePanelContent,
  LogoPanel,
  CustomItemPanel,
  SNPanel,
  OptionsPanel
} from './FormItems'
import { deleteStorage } from '../../lib/draft'
import { subWatermarkText } from '../../../../utils/utils'
import { capturePromiseException } from '../../../../utils/sentryUtils'
import crown_golden from '../../../../images/crown_golden.png'
import {
  useAppDispatch,
  useAppSelector,
  usePayment,
  useVip
} from '../../../../app/hooks'
import DepartmentUserPicker from '../../../components/DepartmentMemberPicker'
import type { SelectedDepartment } from '../../../components/DepartmentMemberPicker'
import CONFIG from '../../../../constants/config'
import { coordinateBeforeEditAndSubmit } from './BatchImportGeoData/lib'

// 地理位置项类型
interface GeoItem {
  watermarkID?: string
  itemID: number | string
  content: string
  uploadLocation?: any
}

// 级联选项类型
interface CascadeItem {
  itemId: number | string
  records: any[]
}

// 转换后的条目类型
interface TransformedItem {
  itemId: number | string
  records: Array<{ content: string; uploadLocation?: any }>
}

// Grant 类型
interface GrantType {
  departmentIDs?: string[]
  departmentUserIDs?: string[]
  userIDs?: string[]
}

interface GrantProps {
  style?: React.CSSProperties
  watermark?: any
  onGrantChange: (grant: GrantType) => void
  clickEvent: (event: string) => void
}

const Grant = ({ style, onGrantChange, clickEvent }: GrantProps) => {
  const { action, watermark_id, is_draft } = parseQueryString()
  const [departmentUserPickerVisible, setDepartmentUserPickerVisible] =
    useState(false)

  const [filterObj, setFilterObj] = useState<{
    members: string[]
    userDepartmentIDs: string[]
    departments: SelectedDepartment[]
  }>({
    members: [],
    userDepartmentIDs: [],
    departments: []
  })

  const [tips, setTips] = useState('')

  const dispatch = useAppDispatch()
  const groupID = useAppSelector((state) => state.group.groupId)

  const watermarkList = useAppSelector((state) => state.watermark.watermarkList)

  const groupWatermark = useMemo(() => {
    if (action === 'modify') {
      return watermarkList.find((watermark) => watermark.id === watermark_id)
    } else {
      return null
    }
  }, [action, watermarkList, watermark_id])

  useEffect(() => {
    if (action === 'new' || is_draft === '1') {
      setTips('全部成员')
    } else {
      setTips(groupWatermark?.grant?.tips || '全部成员')
    }
  }, [action, is_draft, groupWatermark])

  useEffect(() => {
    if (action === 'modify' && groupWatermark) {
      setFilterObj({
        members: [],
        userDepartmentIDs: groupWatermark.grant?.departmentUserIDs || [],
        departments: groupWatermark.grant?.departments || []
      })
    }
  }, [action, groupWatermark])

  const isVip = useAppSelector((state) => state.group.isVip)

  const onGrantClick = useCallback(() => {
    setDepartmentUserPickerVisible(true)
    clickEvent('memberUseLimit')
  }, [])

  const onGrant = (grant: {
    selectedNormalUserIDs: string[]
    selectedDepartmentUserIDs: string[]
    selectedDepartments: SelectedDepartment[]
  }) => {
    const departmentIDs =
      grant?.selectedDepartments.map(
        (department: SelectedDepartment) => department.id
      ) || []
    const departmentUserIDs = grant?.selectedDepartmentUserIDs || []
    const userIDs = grant?.selectedNormalUserIDs || []
    setFilterObj({
      members: userIDs,
      userDepartmentIDs: departmentUserIDs,
      departments: grant?.selectedDepartments || []
    })

    dispatch(
      watermarkGrantTips({
        groupID,
        departmentIDs,
        departmentUserIDs,
        userIDs
      })
    ).then((data) => {
      setTips(data.tips)
    })

    onGrantChange({
      departmentIDs,
      departmentUserIDs,
      userIDs
    })
    setDepartmentUserPickerVisible(false)
  }

  return (
    <div className="grant grant-vip" style={style}>
      <img src={crown_golden} alt="" />
      <span>水印使用范围：</span>
      <a className="granted-users" onClick={onGrantClick}>
        {tips + ' >'}
      </a>
      <DepartmentUserPicker
        title="设置水印使用范围"
        open={departmentUserPickerVisible}
        onlyInGroup
        vipRequired
        // limitedTimeFree // 3.0.50版本后，不再支持限时免费
        onCancel={() => setDepartmentUserPickerVisible(false)}
        onOk={onGrant}
        filterObj={filterObj}
      />
    </div>
  )
}

const MaxLimit = ({
  visible,
  watermarkCount,
  afterSaveWatermarkCount,
  clickEvent
}) => {
  const groupID = useAppSelector((state) => state.group.groupId)
  const payment = usePayment()
  const { isVip, isSVip, isVVip } = useVip()
  const onPay = () => {
    // 非vip单独一套逻辑
    if (!isVip && !isVVip && !isSVip) {
      if (afterSaveWatermarkCount >= 8) {
        payment.openSvip('WEBGroupWatermarkNumSvip', false, true, false)
      } else if (afterSaveWatermarkCount >= 5) {
        payment.openSvip('WEBGroupWatermarkNumVvip', false, false, true)
      } else {
        payment.open('WEBGroupWatermarkNumVip')
      }
    } else if (
      !isSVip &&
      afterSaveWatermarkCount >= CONFIG.vipPrivilege.watermarkNum.vip
    ) {
      //只要不是SVIP，且水印数量大于等于8，就开通SVIP
      payment.openSvip('WEBGroupWatermarkNum7', false, false, true)
    } else {
      payment.open('WEBGroupWatermarkNum')
    }
  }

  const text = useMemo(() => {
    if (
      !isVip &&
      afterSaveWatermarkCount > CONFIG.vipPrivilege.watermarkNum.noVip
    ) {
      return (
        <span>
          已有{watermarkCount}个团队水印，<strong>开通会员</strong>
          享更多水印权益（基础会员可发布
          {4}
          个团队水印，高级会员可发布{8}
          个团队水印，超级会员不限量发布）
        </span>
      )
    }
    if (
      isVip &&
      !isVVip &&
      !isSVip &&
      afterSaveWatermarkCount >= CONFIG.vipPrivilege.watermarkNum.vip
    ) {
      return (
        <span>
          已有{watermarkCount}个团队水印，开通
          <strong>团队高级会员</strong>
          不限量发布团队水印
        </span>
      )
    }
    return ''
  }, [watermarkCount, isVip, isSVip, afterSaveWatermarkCount])

  if (visible) {
    sensors.track('web_show_page_openapp_workgroupvip', {
      groupID: groupID,
      place: parsePlaceValue(),
      functionType: 'watermarkNum'
    })
    return (
      <div className="max-limit">
        {text}
        <Button
          type="link"
          onClick={() => {
            sensors.track('web_click_button_workgroup_go_vip_trigger', {
              place: parsePlaceValue(),
              functionType: 'watermarkNum',
              groupID
            })
            onPay()
          }}
        >
          <strong>[立即开通]</strong>
        </Button>
      </div>
    )
  }
  return null
}

const CascadeVipRequire = ({ visible }) => {
  const groupID = useAppSelector((state) => state.group.groupId)
  const payment = usePayment()
  const { isVip, isSVip } = useVip()
  const onPay = () => {
    payment.open('groupWatermarkMultiOption')
  }

  if (visible) {
    sensors.track('web_show_page_openapp_workgroupvip', {
      groupID: groupID,
      place: parsePlaceValue(),
      functionType: 'groupWatermarkMultiOption'
    })
    return (
      <div className="max-limit">
        <span>
          预设多级选项为会员功能，<strong>开通会员</strong>
          解锁限制，享更多水印权益
        </span>
        <Button
          type="link"
          onClick={() => {
            sensors.track('web_click_button_workgroup_go_vip_trigger', {
              place: parsePlaceValue(),
              functionType: 'groupWatermarkMultiOption',
              groupID
            })
            onPay()
          }}
        >
          <strong>[立即开通]</strong>
        </Button>
      </div>
    )
  }
  return null
}

const MaxLimitBuyButton = ({ afterSaveWatermarkCount, clickEvent }) => {
  const payment = usePayment()
  const { isVip, isSVip, isVVip } = useVip()
  const onPay = () => {
    // 非vip 单独一套逻辑
    if (!isVip && !isVVip && !isSVip) {
      if (afterSaveWatermarkCount >= 8) {
        clickEvent('WEBGroupWatermarkNumSvip')
        payment.openSvip('WEBGroupWatermarkNumSvip', false, true, false)
      } else if (afterSaveWatermarkCount >= 5) {
        clickEvent('WEBGroupWatermarkNumVvip')
        payment.openSvip('WEBGroupWatermarkNumVvip', false, false, true)
      } else {
        clickEvent('WEBGroupWatermarkNumVip')
        payment.open('WEBGroupWatermarkNum')
      }
    } else if (
      !isSVip &&
      afterSaveWatermarkCount >= CONFIG.vipPrivilege.watermarkNum.vip
    ) {
      //只要不是SVIP，且水印数量大于等于8，就开通SVIP
      payment.openSvip('WEBGroupWatermarkNum7', false, false, true)
    } else {
      payment.open('WEBGroupWatermarkNum')
    }
  }
  return (
    <Button className="max-limit-buy-button" onClick={onPay}>
      开通后发布
      <img src={crown_golden} alt="" />
    </Button>
  )
}

const customPanelStyle = {
  marginTop: '12px',
  borderRadius: 0,
  backgroundColor: '#F5F8FA',
  overflow: 'hidden'
}

function FormPanel({
  webNotSupportItems,
  clickEvent,
  activeTabKey,
  onTabChange
}) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.main.user)
  const groupId = useAppSelector((state) => state.group.groupId)
  const itemCnt = useAppSelector((state) => state.group.itemCnt)
  const watermark = useAppSelector((state) => state.editor.watermark)
  const geoItemsList = useAppSelector((state) => state.editor.geoItemsList)
  const cascadeItems = useAppSelector((state) => state.editor.cascadeItems)
  const expandedPanels = useAppSelector((state) => state.editor.expandedPanels)
  const watermarkList = useAppSelector((state) => state.watermark.watermarkList)
  const config = useAppSelector((state) => state.main.config)
  const isVip = useAppSelector((state) => state.group.isVip)
  const isSVip = useAppSelector(
    (state) =>
      state.group.isVip &&
      state.group.vipInfo?.vip_kind === CONFIG.VIP_KIND.SVIP
  )
  const isVVip = useAppSelector(
    (state) =>
      state.group.isVip &&
      state.group.vipInfo?.vip_kind === CONFIG.VIP_KIND.VVIP
  )

  const [logoSourceSrc, setLogoSourceSrc] = useState('')
  const [grant, setGrant] = useState<GrantType | null>(null)

  const prevGroupIdRef = useRef(groupId)

  // componentDidUpdate - 切换团队时刷新水印列表
  useEffect(() => {
    if (prevGroupIdRef.current !== groupId) {
      prevGroupIdRef.current = groupId
      dispatch(
        fetchGroupWatermarks({
          userID: user?.user_id,
          groupID: groupId
        })
      )
    }
  }, [dispatch, groupId, user?.user_id])

  const handlePickImage = (image) => setLogoSourceSrc(image)
  const handleCropComplete = () => setLogoSourceSrc('')

  // 将geoItemsList转换为接口需要的格式
  const transformItemsList = useCallback(
    (newGeoItemsList: GeoItem[]): TransformedItem[] => {
      const items: TransformedItem[] = []
      newGeoItemsList.forEach((geoItem) => {
        const index = items.findIndex((item) => item.itemId === geoItem.itemID)
        if (index === -1) {
          items.push({
            itemId: geoItem.itemID,
            records: [
              {
                content: geoItem.content,
                uploadLocation: geoItem.uploadLocation
              }
            ]
          })
        } else {
          items[index].records.push({
            content: geoItem.content,
            uploadLocation: geoItem.uploadLocation
          })
        }
      })
      return items
    },
    []
  )

  // 将多级选项转换为接口需要的格式
  const transformMultipleItems = useCallback(() => {
    const treeToPlain = (tree: any[], currentPath = ''): string[] => {
      let result: string[] = []
      tree.forEach((item) => {
        if (!item.content) return
        const content = item.content.replace(/\//g, '%2F')
        const newPath = currentPath ? `${currentPath}/${content}` : content
        if (item.childs && item.childs.length) {
          result = result.concat(treeToPlain(item.childs, newPath))
        } else {
          result.push(newPath)
        }
      })
      return result
    }

    const items = cascadeItems.map((cascadeItem) => {
      const plainData = treeToPlain(cascadeItem.records)
      return {
        itemId: cascadeItem.itemId,
        records: plainData.map((content: string) => {
          return {
            content
          }
        })
      }
    })
    return items
  }, [cascadeItems])

  // 检查多级选项是否有空条目
  const checkMultipleEmptyItems = useCallback(
    (cascadeItemsToCheck: CascadeItem[]) => {
      const items = watermark.items
      const loop = (tree: any[]): boolean => {
        return tree.some((item) => {
          if (item.childs && item.childs.length) {
            return loop(item.childs)
          }
          return item.content === ''
        })
      }

      const emptyCascadeItem = cascadeItemsToCheck.find((cascadeItem) => {
        const watermarkItem = items.find(
          (item: any) => item.id === cascadeItem.itemId
        )
        if (watermarkItem && watermarkItem.switchStatus) {
          return loop(cascadeItem.records)
        }
        return false
      })
      return emptyCascadeItem
    },
    [watermark]
  )

  // 检查预设选项是否有空条目
  const checkItemsListEmptyItem = useCallback((newGeoItemsList: GeoItem[]) => {
    const emptyItem = newGeoItemsList.find((geoItem) => {
      return geoItem.content === ''
    })
    return emptyItem ? emptyItem.itemID : ''
  }, [])

  // 检查预设选项是否超过1000条
  const checkItemsListOverLimit = useCallback(
    (newGeoItemsList: GeoItem[]) => {
      const itemLimit = parseInt(
        String(itemCnt || config?.normal_group_option_limit || 1000)
      )
      const groupedCount = newGeoItemsList.reduce(
        (acc: Record<string, number>, cur) => {
          const key = String(cur.itemID)
          if (!acc[key]) {
            acc[key] = 1
          } else {
            acc[key] += 1
          }
          return acc
        },
        {}
      )
      const exceedingItemID = Object.entries(groupedCount).find(
        ([itemID, count]) => count > itemLimit
      )?.[0]
      return exceedingItemID && Number(exceedingItemID)
    },
    [itemCnt, config]
  )

  // 检查多级选项是否超过1000条
  const checkMultipleItemsOverLimit = useCallback(
    (cascadeItemsToCheck: CascadeItem[]) => {
      const itemLimit = parseInt(
        String(itemCnt || config?.normal_group_option_limit || 1000)
      )
      const loopCount = (tree: any[]): number => {
        return tree.reduce((acc, cur) => {
          if (cur.childs && cur.childs.length) {
            return acc + 1 + loopCount(cur.childs)
          }
          return acc + 1
        }, 0)
      }

      const groupedCount = cascadeItemsToCheck.map((cascadeItem) => {
        return {
          itemID: cascadeItem.itemId,
          count: loopCount(cascadeItem.records)
        }
      })
      const exceedingItemID = groupedCount.find((item) => {
        return item.count > itemLimit
      })?.itemID
      return exceedingItemID && Number(exceedingItemID)
    },
    [itemCnt, config]
  )

  const handleSubmit = useCallback(() => {
    const { action, is_draft } = parseQueryString()
    clickEvent('post')
    const itemLimit = parseInt(
      itemCnt || config?.normal_group_option_limit || 1000
    )
    const groupWatermark = watermarkList.find(
      (gWatermark) => gWatermark.id === watermark.id
    )
    let items = [...watermark.items]

    if (!watermark.name) {
      Modal.warning({
        title: '请填写水印模板标题！'
      })
      return false
    }

    if (
      items.some((item) => item.contentEdit?.editType === 1 && !item.content)
    ) {
      Modal.warning({
        title: '您有未填写的内容',
        content: '请填写必填内容后，再发布该水印。'
      })
      return false
    }

    items = items.concat(webNotSupportItems)

    let filteredWatermark = {
      ...watermark,
      items,
      groupId
    }
    if (filteredWatermark.logo?.url === '') {
      filteredWatermark = {
        ...filteredWatermark,
        logo: {
          ...filteredWatermark.logo,
          switchStatus: false
        }
      }
    }
    if (filteredWatermark.logo?.switchStatus === false) {
      filteredWatermark = {
        ...filteredWatermark,
        logo: {
          ...filteredWatermark.logo,
          url: ''
        }
      }
    }

    const openedItemsID: (number | string)[] = []
    const openedCascadeItemsID: (number | string)[] = []

    items.forEach((item: any, index: number) => {
      if (item.contentEdit?.itemsList?.length) {
        const newItemsList = item.contentEdit.itemsList.filter(
          (listItem: string) => listItem.trim()
        )
        items[index] = {
          ...item,
          contentEdit: {
            ...item.contentEdit,
            itemsList: newItemsList
          }
        }
      }

      if (item.switchStatus) {
        if (item.contentEdit?.editType === 2) {
          openedItemsID.push(item.id)
        } else if (item.contentEdit?.editType === 3) {
          openedCascadeItemsID.push(item.id)
        }
      }
    })

    filteredWatermark = {
      ...filteredWatermark,
      items
    }

    const newGeoItemsList = coordinateBeforeEditAndSubmit(
      filteredWatermark,
      geoItemsList
    ).filter((item: GeoItem) => {
      return openedItemsID.includes(item.itemID)
    })

    const cascadeItemsFiltered = cascadeItems.filter((item) => {
      return openedCascadeItemsID.includes(item.itemId)
    })

    const exceedingItemID = checkItemsListOverLimit(newGeoItemsList)
    const exceedingCascadeItemID =
      checkMultipleItemsOverLimit(cascadeItemsFiltered)
    if (exceedingItemID || exceedingCascadeItemID) {
      const itemTitle = items.find(
        (item) =>
          item.id === exceedingCascadeItemID || item.id === exceedingItemID
      )?.title
      Modal.warning({
        title: `最多支持${itemLimit}条预设选项`,
        content: `「${itemTitle}」的预设选项内容过多，请调整选项内容`
      })
      return false
    }

    const emptyItemID = checkItemsListEmptyItem(newGeoItemsList)
    const emptyCascadeItem = checkMultipleEmptyItems(cascadeItemsFiltered)
    if (emptyCascadeItem || emptyItemID) {
      const itemTitle = items.find(
        (item) =>
          item.id === emptyCascadeItem?.itemId || item.id === emptyItemID
      )?.title
      Modal.warning({
        title: '您有未填写的内容',
        content: `「${itemTitle}」的预设选项内容为空，请完善选项内容`
      })
      return false
    }

    const usedItems = items
      .filter((item) => item.switchStatus)
      .map((item) => item.id)
    if (filteredWatermark.logo?.switchStatus) {
      usedItems.push(filteredWatermark.logo.id)
    }
    if (filteredWatermark.theme?.switchStatus) {
      usedItems.push(filteredWatermark.theme.id)
    }

    dispatch(
      updateOrNewWatermark({
        userID: user?.user_id,
        groupID: groupId,
        watermark: {
          id: filteredWatermark.id,
          baseID: filteredWatermark.base_id,
          content: JSON.stringify(filteredWatermark),
          updateTime:
            action === 'new' || is_draft === '1'
              ? ''
              : groupWatermark?.updateTime,
          grant: {
            departmentIDs: grant?.departmentIDs,
            departmentUserIDs: grant?.departmentUserIDs,
            userIDs: grant?.userIDs
          }
        },
        insInfo: {
          operateTemplateID: '',
          fromPlace: 'webWatermarkManage',
          isAutoCreateGroup: 0
        },
        usedItems: usedItems
      })
    )
      .then(
        () => is_draft === '1' && deleteStorage(filteredWatermark.id, groupId)
      )
      .then(() => {
        dispatch(
          saveWatermarkItemsList({
            userID: user?.user_id,
            groupID: groupId,
            watermarkId: filteredWatermark.id,
            multipleItems: transformMultipleItems(),
            items: transformItemsList(newGeoItemsList)
          })
        )
      })
      .then(() => navigate('/watermark/list', { replace: true }))
      .then(() => message.success('操作成功！'))
      .then(() => {
        sensors.track('web_workgroup_watermark_add_suc', {
          groupID: groupId,
          WatermarkID: filteredWatermark.id,
          baseID: filteredWatermark.base_id,
          EditType:
            action === 'new' || is_draft === '1'
              ? 'createWatermark'
              : 'modifyWatermark',
          UsedItem: usedItems
        })
      })
      .catch(capturePromiseException)
  }, [
    clickEvent,
    itemCnt,
    config,
    watermarkList,
    watermark,
    webNotSupportItems,
    groupId,
    geoItemsList,
    cascadeItems,
    grant,
    user?.user_id,
    dispatch,
    checkItemsListOverLimit,
    checkMultipleItemsOverLimit,
    checkItemsListEmptyItem,
    checkMultipleEmptyItems,
    transformMultipleItems,
    transformItemsList
  ])

  const handleCancel = useCallback(() => {
    clickEvent('cancel')
    Modal.confirm({
      title: '编辑内容未发布，是否离开？',
      content: '离开后将不保存修改内容。',
      onOk: () => navigate('/watermark/list', { replace: true })
    })
  }, [clickEvent])

  const renderLogo = () => {
    const isSupportLogo = !!WATERMARK_CONFIG[watermark.base_id].logo
    if (watermark.logo && watermark.logo?.switchStatus && isSupportLogo) {
      return (
        <LogoPanel
          style={customPanelStyle}
          key={String(watermark.logo?.id)}
          id={String(watermark.logo?.id)}
          logoSourceSrc={logoSourceSrc}
          onPickImage={handlePickImage}
          onCropComplete={handleCropComplete}
        />
      )
    }
    return null
  }

  const renderItems = () => {
    return watermark.items.map((item, index) => {
      if (
        WATERMARK_CONFIG[watermark.base_id].optionItems.includes(item.id) &&
        item.switchStatus
      ) {
        return (
          <OptionsPanel
            style={customPanelStyle}
            key={String(item.id)}
            id={String(item.id)}
            index={index}
            item={item}
            clickEvent={clickEvent}
          />
        )
      }
      if (item.id === ITEM_ID.ID_410 && item.switchStatus) {
        return (
          <SNPanel
            style={customPanelStyle}
            key={String(item.id)}
            id={String(item.id)}
            index={index}
            item={item}
          />
        )
      }
      if (
        !WATERMARK_CONFIG[watermark.base_id].optionItems.includes(item.id) &&
        item.switchStatus
      ) {
        return (
          <CustomItemPanel
            style={customPanelStyle}
            key={String(item.id)}
            id={String(item.id)}
            index={index}
            item={item}
            clickEvent={clickEvent}
          />
        )
      }
      return null
    })
  }

  const { action, is_draft } = parseQueryString()

  const isNew = action === 'new' || (action === 'modify' && is_draft === '1')
  const afterSaveWatermarkCount = isNew
    ? watermarkList.length + 1
    : watermarkList.length
  const showMaxLimitOpenVipTip =
    (!isVip &&
      afterSaveWatermarkCount > CONFIG.vipPrivilege.watermarkNum.noVip) ||
    (!isSVip &&
      !isVVip &&
      isVip &&
      afterSaveWatermarkCount > CONFIG.vipPrivilege.watermarkNum.vip)

  if (watermark) {
    const showCascadeVipRequire =
      !isVip &&
      watermark.items.some((item) => {
        return (
          item.editType === 2 &&
          item.contentEdit?.editType === 3 &&
          item.switchStatus
        )
      })
    return (
      <div className="form-panel" id="watermark-editor-form-panel">
        <div style={{ padding: '16px 16px 80px 16px' }}>
          <Tabs activeKey={activeTabKey || 'content'} onChange={onTabChange}>
            <Tabs.TabPane
              tab={
                <span>
                  <EditOutlined />
                  水印模版
                </span>
              }
              key="content"
            >
              <div className="form-panel-content">
                <Collapse
                  onChange={(keys) => {
                    dispatch(setExpandedPanels(keys))
                    if (keys.length > expandedPanels.length) {
                      clickEvent('openItemEdit')
                    } else if (keys.length < expandedPanels.length) {
                      clickEvent('closeItemEdit')
                    }
                  }}
                  activeKey={expandedPanels}
                  bordered={false}
                >
                  {renderLogo()}
                  {renderItems()}
                </Collapse>
              </div>
            </Tabs.TabPane>
            {watermark.theme &&
              WATERMARK_CONFIG[watermark.base_id] &&
              (WATERMARK_CONFIG[watermark.base_id] as any)?.theme && (
                <Tabs.TabPane
                  tab={
                    <span>
                      <BgColorsOutlined />
                      {
                        (WATERMARK_CONFIG[watermark.base_id] as any)?.theme
                          ?.displayName
                      }
                    </span>
                  }
                  key="theme"
                >
                  <ThemePanelContent clickEvent={clickEvent} />
                </Tabs.TabPane>
              )}
          </Tabs>
        </div>
        <div className="form-panel-footer">
          <Grant
            style={
              showMaxLimitOpenVipTip || showCascadeVipRequire
                ? { bottom: '82px' }
                : {}
            }
            onGrantChange={(g: GrantType) => setGrant(g)}
            clickEvent={clickEvent}
          />
          <MaxLimit
            visible={showMaxLimitOpenVipTip}
            watermarkCount={watermarkList.length}
            afterSaveWatermarkCount={afterSaveWatermarkCount}
            clickEvent={clickEvent}
          />
          <CascadeVipRequire visible={showCascadeVipRequire} />
          <div className="editor-footer">
            <Button style={{ marginRight: '10px' }} onClick={handleCancel}>
              取消
            </Button>
            {showMaxLimitOpenVipTip || showCascadeVipRequire ? (
              <MaxLimitBuyButton
                afterSaveWatermarkCount={afterSaveWatermarkCount}
                clickEvent={clickEvent}
              />
            ) : (
              <Button
                type="primary"
                disabled={
                  !watermark.logo?.switchStatus &&
                  watermark.items.every((item) => item.switchStatus === false)
                }
                onClick={handleSubmit}
              >
                发布
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }
  return <div className="form-panel"></div>
}

export default FormPanel
